/**
 * Admin service — user management operations via Edge Function
 * All operations require the caller to have the 'admin' role.
 */

import { supabase } from './supabase';

async function callAdminFunction(action, payload = {}) {
  const { data, error } = await supabase.functions.invoke('admin-users', {
    body: { action, ...payload },
  });
  if (error) throw new Error(error.message || 'Admin operation failed');
  return data;
}

export const adminService = {
  /**
   * List all users with their profile (role, is_active, etc.)
   * @returns {Promise<Array>}
   */
  listUsers: () => callAdminFunction('list').then(d => d.users),

  /**
   * Create a new user account.
   * @param {Object} params
   * @param {string} params.email
   * @param {string} params.password
   * @param {string} [params.displayName]
   * @param {string} [params.role] - 'admin' | 'user'
   */
  createUser: ({ email, password, displayName, role }) =>
    callAdminFunction('create', { email, password, displayName, role }),

  /**
   * Update an existing user's email, display name, or role.
   * @param {string} userId
   * @param {Object} updates - { email?, displayName?, role? }
   */
  updateUser: (userId, updates) =>
    callAdminFunction('update', { userId, ...updates }),

  /**
   * Enable or disable a user account.
   * @param {string} userId
   * @param {boolean} isActive
   */
  setActive: (userId, isActive) =>
    callAdminFunction('set_active', { userId, isActive }),
};
